import { produce } from 'immer'
import { create } from 'zustand'

import { createDefaultPanel, NO_GHOST_FILTERS } from 'features/charts-v2/store'
import type {
  NumericOperatorType,
  OperatorType,
  SelectedFilterOption,
} from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'

// ---------------------------------------------------------------------------
// Catalog's filter state is the same `ChartPanel` shape charts-v2/Feedback/
// Keywords use — see `features/feedback/store.ts`'s doc comment for why
// reusing the type is the right move (existing v1<->v2 translators, panel
// filter-option counts, keyword/smart-search plumbing all come for free).
//
// Unlike Feedback/Keywords, catalog has no comparative-panel concept — there
// is exactly one filter panel for the whole page, never a "Panel 1/2"
// switcher. `panels` is still an array (rather than a bare `panel: ChartPanel`
// field) so this store can reuse the same per-panel-id action shapes
// (`updatePanel(panelId, ...)`, `updateFilterSelection(panelId, ...)`, etc.)
// `FilterControlProps`-building code across the app already expects — it
// simply never grows past length 1.
// ---------------------------------------------------------------------------

export type CatalogFiltersState = {
  panels: ChartPanel[]
  activePanelId: string | null
  /** Eye-toggled-off filters for the single panel. See `charts-v2/store.ts`'s
   *  `ghostFilters` doc comment for why this is a sibling of `panels`, never
   *  persisted onto a filter itself. */
  ghostFilters: Record<string, SelectedFilterOption[]>

  hydrate: (state: {
    panels: ChartPanel[]
    activePanelId: string | null
  }) => void
  updatePanel: (panelId: string, updates: Partial<ChartPanel>) => void
  updateFilterSelection: (
    panelId: string,
    field: string,
    operator: OperatorType | NumericOperatorType,
    values: string[],
    range?: {
      min?: number
      max?: number
      valueType?: number
      numericValue?: number
    },
  ) => void
  disableFilter: (panelId: string, field: string) => void
  enableFilter: (panelId: string, field: string) => void
  dropGhost: (panelId: string, field: string) => void
  toHashPayload: () => { panels: ChartPanel[]; activePanelId: string | null }
  /** Wholesale replace of everything the filter sidebar edits — used only by
   *  undo/redo, which restores the ghost stash with the panel it mirrors. */
  restore: (state: {
    panels: ChartPanel[]
    activePanelId: string | null
    ghostFilters: Record<string, SelectedFilterOption[]>
  }) => void
}

const seedPanel = createDefaultPanel(0)

const initialCatalogFiltersState = {
  panels: [seedPanel] as ChartPanel[],
  activePanelId: seedPanel.id as string | null,
}

/** Same rule as `charts-v2/store.ts`'s `pruneGhostFilters`: every
 *  `ghostFilters` entry names a live panel, and only fields NOT currently in
 *  that panel's `filters`. Run after every mutation that can replace the
 *  panel's filters. */
function pruneGhostFilters(state: CatalogFiltersState): void {
  for (const panelId of Object.keys(state.ghostFilters)) {
    const panel = state.panels.find((p) => p.id === panelId)
    if (!panel) {
      delete state.ghostFilters[panelId]
      continue
    }
    const liveFields = new Set(panel.filters.map((f) => f.field))
    const ghosts = state.ghostFilters[panelId]!
    const kept = ghosts.filter((f) => !liveFields.has(f.field))
    if (kept.length === 0) {
      delete state.ghostFilters[panelId]
    } else if (kept.length !== ghosts.length) {
      state.ghostFilters[panelId] = kept
    }
  }
}

export const useCatalogFiltersStore = create<CatalogFiltersState>()((
  set,
  get,
) => {
  const setPanels = (recipe: (state: CatalogFiltersState) => void): void => {
    set(
      produce((state: CatalogFiltersState) => {
        recipe(state)
        pruneGhostFilters(state)
      }),
    )
  }

  return {
    ...initialCatalogFiltersState,
    ghostFilters: {},

    restore: ({ panels, activePanelId, ghostFilters }) =>
      set({ panels, activePanelId, ghostFilters }),

    hydrate: ({ panels, activePanelId }) => {
      set(
        produce((state: CatalogFiltersState) => {
          state.panels = panels
          state.activePanelId = activePanelId
          // Mirrors charts-v2/Feedback: never a blanket clear, only drop
          // ghosts for panels that no longer exist.
          for (const panelId of Object.keys(state.ghostFilters)) {
            if (!panels.some((p) => p.id === panelId)) {
              delete state.ghostFilters[panelId]
            }
          }
        }),
      )
    },

    updatePanel: (panelId, updates) => {
      setPanels((state) => {
        const panelIndex = state.panels.findIndex((p) => p.id === panelId)
        if (panelIndex !== -1) {
          state.panels[panelIndex] = {
            ...state.panels[panelIndex]!,
            ...updates,
          }
        }
      })
    },

    updateFilterSelection: (panelId, field, operator, values, range) => {
      setPanels((state) => {
        const panel = state.panels.find((p) => p.id === panelId)
        if (!panel) return

        const existingFilters = panel.filters || []
        const existingIndex = existingFilters.findIndex(
          (f) => f.field === field,
        )

        // Detect delete: empty values AND no range of any kind. Window
        // filters (score/post_score) send empty values + {min,max,valueType}
        // and numeric filters send empty values + {numericValue}; either is
        // an update, not a delete. Mirrors charts-v2/store.ts verbatim.
        const isDeleteOperation =
          values.length === 0 &&
          range?.numericValue === undefined &&
          range?.min === undefined &&
          range?.max === undefined &&
          range?.valueType === undefined

        if (isDeleteOperation && existingIndex >= 0) {
          panel.filters = existingFilters.filter((f) => f.field !== field)
          return
        }

        const newFilter = {
          field,
          operator,
          values,
          ...(range || {}),
        }

        let newFilters
        if (existingIndex >= 0) {
          newFilters = [...existingFilters]
          newFilters[existingIndex] = newFilter
        } else {
          newFilters = [...existingFilters, newFilter]
        }

        panel.filters = newFilters
      })
    },

    disableFilter: (panelId, field) => {
      setPanels((state) => {
        const panel = state.panels.find((p) => p.id === panelId)
        if (!panel) return
        const index = panel.filters.findIndex((f) => f.field === field)
        if (index === -1) return
        const [ghosted] = panel.filters.splice(index, 1)
        if (!ghosted) return
        const existing = state.ghostFilters[panelId] ?? []
        state.ghostFilters[panelId] = [
          ...existing.filter((f) => f.field !== field),
          ghosted,
        ]
      })
    },

    enableFilter: (panelId, field) => {
      setPanels((state) => {
        const ghosts = state.ghostFilters[panelId]
        if (!ghosts) return
        const index = ghosts.findIndex((f) => f.field === field)
        if (index === -1) return
        const [restored] = ghosts.splice(index, 1)
        if (!restored) return
        if (ghosts.length === 0) delete state.ghostFilters[panelId]
        const panel = state.panels.find((p) => p.id === panelId)
        if (!panel) return
        panel.filters = [
          ...panel.filters.filter((f) => f.field !== field),
          restored,
        ]
      })
    },

    dropGhost: (panelId, field) => {
      setPanels((state) => {
        const ghosts = state.ghostFilters[panelId]
        if (!ghosts) return
        const kept = ghosts.filter((f) => f.field !== field)
        if (kept.length === 0) delete state.ghostFilters[panelId]
        else state.ghostFilters[panelId] = kept
      })
    },

    toHashPayload: () => {
      const state = get()
      return { panels: state.panels, activePanelId: state.activePanelId }
    },
  }
})

/** Stable empty-array re-export so `ghostFilters[panelId] ?? NO_GHOST_FILTERS`
 *  selectors never mint a fresh identity. Same constant charts-v2 uses. */
export { NO_GHOST_FILTERS }
